import { createApiClient, type Schemas } from '@mock-sensors/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SPACE } from '@/lib/space';

export const api = createApiClient(SPACE);

export type Instance = Schemas['InstanceDto'];
export type Definition = Schemas['DefinitionSummary'];
export type DefinitionDetail = Schemas['DefinitionDetail'];
export type Binding = Schemas['BindingDto'];
export type BindingConfig = Schemas['BindingConfig'];
export type Sample = Schemas['SampleDto'];
export type Field = Schemas['FieldDto'];
export type Meta = Schemas['Meta'];
export type Protocol = Schemas['Protocol'];
export type Problem = Schemas['Problem'];
export type AmbientMetric = Schemas['AmbientMetricDto'];
export type Preset = Schemas['PresetDto'];
export type Dataset = Schemas['DatasetDto'];
export type Setpoint = Schemas['SetpointDto'];
export type AmbientStatus = Schemas['AmbientStatus'];
export type Job = Schemas['JobDto'];
export type Scenario = Schemas['ScenarioDto'];
export type ScenarioRun = Schemas['RunDto'];
export type ScenarioStep = Schemas['ScenarioStep'];

/** A failed API call, carrying the RFC 9457 problem body when there is one. */
export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly problem: Partial<Problem> = {},
	) {
		super(problem.detail ?? problem.title ?? `HTTP ${status}`);
	}

	/** Field errors keyed by JSON pointer, optionally re-rooted under `prefix`. */
	fieldErrors(prefix = ''): Record<string, string> {
		const out: Record<string, string> = {};
		for (const e of this.problem.errors ?? []) {
			if (e.path.startsWith(prefix)) out[e.path.slice(prefix.length) || '/'] = e.message;
		}
		return out;
	}
}

let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(f: () => void) {
	onUnauthorized = f;
}

/** Unwrap an openapi-fetch result, throwing `ApiError` on failure. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
	const { data, error, response } = await p;
	if (!response.ok) {
		if (response.status === 401) onUnauthorized();
		throw new ApiError(response.status, (error ?? {}) as Partial<Problem>);
	}
	return data as T;
}

export const keys = {
	session: ['session'] as const,
	meta: ['meta'] as const,
	status: ['status'] as const,
	definitions: ['definitions'] as const,
	definition: (id: string) => ['definitions', id] as const,
	instances: ['instances'] as const,
	instance: (id: string) => ['instances', id] as const,
	connection: (id: string) => ['instances', id, 'connection'] as const,
	ambient: ['ambient'] as const,
	jobs: ['jobs'] as const,
	datasets: ['datasets'] as const,
	job: (id: string) => ['jobs', id] as const,
};

export const useSession = () => useQuery({ queryKey: keys.session, queryFn: () => unwrap(api.GET('/api/v1/session')), staleTime: 60_000 });
export const useMeta = () => useQuery({ queryKey: keys.meta, queryFn: () => unwrap(api.GET('/api/v1/meta')), staleTime: 60_000 });
export const useStatus = () => useQuery({ queryKey: keys.status, queryFn: () => unwrap(api.GET('/api/v1/status')), refetchInterval: 5_000 });
export const useDefinitions = () =>
	useQuery({ queryKey: keys.definitions, queryFn: () => unwrap(api.GET('/api/v1/definitions')).then((d) => d.items), staleTime: Infinity });
export const useDefinition = (id: string | undefined) =>
	useQuery({
		queryKey: keys.definition(id ?? ''),
		queryFn: () => unwrap(api.GET('/api/v1/definitions/{id}', { params: { path: { id: id! } } })),
		enabled: !!id,
		staleTime: Infinity,
	});
export const useInstances = () =>
	useQuery({ queryKey: keys.instances, queryFn: () => unwrap(api.GET('/api/v1/instances')).then((d) => d.items), refetchInterval: 10_000 });
export const useInstance = (id: string) =>
	useQuery({ queryKey: keys.instance(id), queryFn: () => unwrap(api.GET('/api/v1/instances/{id}', { params: { path: { id } } })) });
export const useConnection = (id: string) =>
	useQuery({
		queryKey: keys.connection(id),
		queryFn: () => unwrap(api.GET('/api/v1/instances/{id}/connection', { params: { path: { id } } })).then((d) => d.items),
	});

/** Invalidate everything instance-related after a write. */
export function useInvalidateFleet() {
	const qc = useQueryClient();
	return () => {
		qc.invalidateQueries({ queryKey: keys.instances });
		qc.invalidateQueries({ queryKey: keys.status });
	};
}

export function useInstanceAction() {
	const invalidate = useInvalidateFleet();
	return useMutation({
		mutationFn: ({ id, action }: { id: string; action: 'start' | 'stop' }) =>
			unwrap(api.POST(`/api/v1/instances/{id}/${action}`, { params: { path: { id } } })),
		onSuccess: invalidate,
	});
}

export function useDeleteInstance() {
	const invalidate = useInvalidateFleet();
	return useMutation({
		mutationFn: (id: string) => unwrap(api.DELETE('/api/v1/instances/{id}', { params: { path: { id } } })),
		onSuccess: invalidate,
	});
}

export const useAmbient = () => useQuery({ queryKey: keys.ambient, queryFn: () => unwrap(api.GET('/api/v1/ambient')), refetchInterval: 5_000 });
export const useDatasets = () => useQuery({ queryKey: keys.datasets, queryFn: () => unwrap(api.GET('/api/v1/datasets')).then((d) => d.items) });
export const useJobs = () => useQuery({ queryKey: keys.jobs, queryFn: () => unwrap(api.GET('/api/v1/jobs')).then((d) => d.items), refetchInterval: 5_000 });
/** Polls every second while the job is queued or running. */
export const useJob = (id: string | undefined) =>
	useQuery({
		queryKey: keys.job(id ?? ''),
		queryFn: () => unwrap(api.GET('/api/v1/jobs/{id}', { params: { path: { id: id! } } })),
		enabled: !!id,
		refetchInterval: (q) => (q.state.data && !['queued', 'running'].includes(q.state.data.status) ? false : 1_000),
	});
